import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Clapperboard, Trophy } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { GalleryCard } from "@/components/GalleryCard";
import { YearFilter } from "@/components/YearFilter";
import { CLUBS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClubPage() {
  const { slug } = useParams();
  const club = CLUBS[slug];
  const [items, setItems] = useState(null);
  const [year, setYear] = useState("All");

  const years = items
    ? ["All", ...[...new Set(items.map((i) => i.year))].sort().reverse()]
    : ["All"];
  const visible =
    items === null ? null : year === "All" ? items : items.filter((i) => i.year === year);

  useEffect(() => {
    if (!club) return;
    setItems(null);
    setYear("All");
    axios
      .get(`${API}/gallery`, { params: { club: slug } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, [slug, club]);

  if (!club) return <Navigate to="/english-is-lekka" replace />;

  return (
    <div data-testid={`club-page-${slug}`}>
      <section className="relative overflow-hidden bg-sand grain py-16">
        <img
          src={club.image}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-sand/30 via-sand/70 to-sand" />
        <div className="relative mx-auto max-w-5xl px-6">
          <Reveal>
            <Link
              to="/english-is-lekka"
              data-testid="back-to-lekka"
              className="inline-flex items-center gap-2 font-heading text-sm font-bold text-coal/60 transition-colors hover:text-ember"
            >
              <ArrowLeft size={15} />
              English is Lekka
            </Link>
            <h1 className="mt-6 font-display text-5xl font-extrabold tracking-tight text-coal sm:text-6xl">
              {club.name}
            </h1>
            <p className="mt-3 font-mono-brand text-xs font-bold uppercase tracking-[0.3em] text-ember">
              {club.tagline}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-14" data-testid="club-story">
        <div className="mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-[1fr_0.7fr] lg:items-start">
          <Reveal>
            <Eyebrow>The story, 2022, 2025</Eyebrow>
            <div className="mt-6 space-y-5">
              {club.description.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-coal-muted">{p}</p>
              ))}
            </div>

            {club.introVideo && (
              <div
                className="mt-9 rounded-3xl border border-coal/5 bg-white p-6 shadow-[0_14px_44px_-24px_rgba(26,29,36,0.35)]"
                data-testid="club-intro-video"
              >
                <p className="font-mono-brand text-[11px] font-bold uppercase tracking-[0.25em] text-ember">
                  Meet your facilitator
                </p>
                <h3 className="mt-2 font-heading text-lg font-bold text-coal">{club.introVideo.title}</h3>
                <p className="mt-1 text-sm text-coal-muted">{club.introVideo.caption}</p>
                <video
                  src={club.introVideo.url}
                  controls
                  preload="metadata"
                  playsInline
                  className="mt-4 aspect-video w-full rounded-2xl bg-black object-cover"
                />
              </div>
            )}

            {club.winner && (
              <div className="mt-9 flex items-center gap-5 rounded-3xl bg-ember-subtle p-6" data-testid="club-winner-card">
                {club.winner.photo ? (
                  <img
                    src={club.winner.photo}
                    alt={club.winner.name}
                    className="h-20 w-20 shrink-0 rounded-full border-2 border-ember object-cover shadow-md"
                    data-testid="club-winner-photo"
                  />
                ) : (
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-ember text-sand">
                    <Trophy size={24} />
                  </span>
                )}
                <div>
                  <p className="font-display text-xl font-extrabold text-coal">{club.winner.name}</p>
                  <p className="text-sm font-semibold text-ember">{club.winner.detail}</p>
                </div>
              </div>
            )}

            {club.poem && (
              <div className="mt-9 rounded-3xl bg-coal p-7 text-sand" data-testid="club-poem-feature">
                <div className="flex items-center gap-3">
                  <Clapperboard size={20} className="text-ember-light" />
                  <p className="font-mono-brand text-[11px] font-bold uppercase tracking-[0.25em] text-ember-light">
                    Learner showcase
                  </p>
                </div>
                <h3 className="mt-4 font-display text-2xl font-extrabold">{club.poem.title}</h3>
                <p className="mt-1 text-sm text-sand/60">A poem by {club.poem.author}</p>
                {club.poem.video ? (
                  <video
                    data-testid="poem-video"
                    src={club.poem.video}
                    controls
                    preload="metadata"
                    playsInline
                    className="mt-5 aspect-video w-full rounded-2xl bg-black object-cover"
                  />
                ) : (
                  <div className="mt-5 flex aspect-video items-center justify-center rounded-2xl border border-dashed border-sand/20 bg-coal-medium/60">
                    <p className="px-6 text-center font-heading text-sm font-semibold text-sand/50">
                      Performance video premiering soon
                    </p>
                  </div>
                )}
              </div>
            )}
          </Reveal>

          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-[2rem] shadow-[0_24px_60px_-30px_rgba(26,29,36,0.5)] lg:sticky lg:top-28">
              <img src={club.image} alt={`${club.name} in action`} className="aspect-[4/5] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {club.films && (
        <section className="border-y border-coal/5 bg-white py-14" data-testid="club-films">
          <div className="mx-auto max-w-7xl px-6">
            <Reveal>
              <Eyebrow>Made by learners</Eyebrow>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-coal sm:text-4xl">
                Learner films
              </h2>
              <p className="mt-3 max-w-xl text-sm text-coal-muted">
                Short films written, shot and edited by Film & Media Club learners.
              </p>
            </Reveal>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {club.films.map((f, i) => (
                <Reveal key={f.url} delay={i * 0.08}>
                  <div
                    className="overflow-hidden rounded-3xl bg-coal shadow-[0_16px_44px_-22px_rgba(26,29,36,0.45)]"
                    data-testid={`learner-film-${i}`}
                  >
                    <video
                      src={f.url}
                      controls
                      preload="metadata"
                      playsInline
                      className="aspect-video w-full bg-black object-cover"
                    />
                    <div className="p-5">
                      <h3 className="font-heading text-base font-bold text-sand">{f.title}</h3>
                      <p className="mt-1 text-xs text-sand/60">{f.note}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-teal-subtle py-14" data-testid="club-gallery">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <Eyebrow>Moments & memories</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-coal sm:text-4xl">
              The {club.name} gallery
            </h2>
            <p className="mt-3 max-w-xl text-sm text-coal-muted">
              Like, comment and share your favourite moments, every reaction tells our learners the
              world is watching them grow.
            </p>
          </Reveal>
          {items !== null && items.length > 0 && (
            <YearFilter years={years} active={year} onChange={setYear} count={visible?.length} testIdPrefix="club" />
          )}
          {visible === null ? (
            <p className="mt-10 text-sm text-coal-muted" data-testid="gallery-loading">Loading gallery...</p>
          ) : visible.length === 0 ? (
            <p className="mt-10 text-sm text-coal-muted" data-testid="gallery-empty">
              {items.length === 0 ? "Photos for this club are on their way." : `No moments from ${year} yet.`}
            </p>
          ) : (
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((item, i) => (
                <GalleryCard key={item.id} item={item} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="font-mono-brand text-xs font-bold uppercase tracking-[0.25em] text-coal-muted">
              Explore the other clubs
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              {Object.values(CLUBS)
                .filter((c) => c.slug !== slug)
                .map((c) => (
                  <Link
                    key={c.slug}
                    to={`/english-is-lekka/clubs/${c.slug}`}
                    data-testid={`other-club-${c.slug}`}
                    className="rounded-full border border-coal/15 px-5 py-2.5 font-heading text-sm font-bold text-coal transition-colors hover:border-ember hover:bg-ember hover:text-sand"
                  >
                    {c.name}
                  </Link>
                ))}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
